import { AspectRatioIcon } from '../common/AspectRatioIcon';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './AspectRatioPickerModal.module.css';
import { ASPECT_RATIO_USE_CASES, ASPECT_RATIO_EXAMPLE_RESOLUTIONS } from '../../utils/aspectRatio';
import { isAspectRatioSupportedBy } from './utils/aspectRatioSupport';
import { Tooltip } from '../common/Tooltip';
import { Film } from 'lucide-react';
import { RecommendedBadge } from '../common/RecommendedBadge';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** 表示するアスペクト比 (セクション分け・並び順はモーダル側で行う) */
  ratios: string[];
  /** 現在選択中の比率 ('none' や未選択は null 扱いで渡す) */
  selectedRatio: string | null;
  /** ネイティブ対応判定に使う選択中モデル */
  modelCode: string;
  /** ヘッダーに表示するモデルの表示名 (未指定なら modelCode をそのまま出す) */
  modelLabel?: string;
  /** 表示名が通称のとき、チップのツールチップに出す正式名称 (例: GPT Image 2.5 Flare) */
  modelTooltip?: string | null;
  onSelect: (ratio: string) => void;
};

// 1行目に固定表示する主要アスペクト比 (この順で表示)
const MAJOR_RATIOS = ['1:1', '1.91:1', '6:5', '16:9', '9:16'];
const COMMON_BANNER_RATIOS = ['6:5', '728:90', '320:50'];
// 横型セクションの先頭に固定する比率 (バナーの定番を目に付く位置に置く)
const LANDSCAPE_LEADING_RATIOS = ['728:90', '320:50'];
// 動画向けの比率。カード左上にフィルムマークを付けて視認させる
const VIDEO_RATIOS = new Set(['16:9', '9:16']);

function parseRatioValue(ratio: string): number | null {
  const [w, h] = ratio.split(':').map(Number);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w / h;
}

type Section = { title: string; ratios: string[] };

/** 'W:H' を反転して 'H:W' にする (逆数ペアの照合用) */
function invertRatio(ratio: string): string {
  const [w, h] = ratio.split(':');
  return `${h}:${w}`;
}

/**
 * セクション構成:
 * - 主要アスペクト比: 1:1 / 1.91:1 / 6:5 / 16:9 / 9:16 (固定順)
 * - 縦型: 正方形に近い順
 * - 横型: 728:90 / 320:50 を先頭に固定し、以降は縦型の逆数ペア (3:4↔4:3 等) を同じ列に揃えた順。
 *   縦型に対応が無い比率は末尾に置く
 */
function buildSections(ratios: string[]): Section[] {
  ratios = [...new Set([...ratios, ...COMMON_BANNER_RATIOS])];
  const available = new Set(ratios);
  const majors = MAJOR_RATIOS.filter((ratio) => available.has(ratio));
  const rest = ratios.filter((ratio) => !MAJOR_RATIOS.includes(ratio));
  const portrait = rest
    .filter((ratio) => (parseRatioValue(ratio) ?? 1) < 1)
    .sort((a, b) => (parseRatioValue(b) ?? 1) - (parseRatioValue(a) ?? 1));
  const landscapeSet = new Set(rest.filter((ratio) => (parseRatioValue(ratio) ?? 1) >= 1));
  const landscape: string[] = [];
  for (const ratio of LANDSCAPE_LEADING_RATIOS) {
    if (landscapeSet.has(ratio)) {
      landscape.push(ratio);
      landscapeSet.delete(ratio);
    }
  }
  for (const ratio of portrait) {
    const inverse = invertRatio(ratio);
    if (landscapeSet.has(inverse)) {
      landscape.push(inverse);
      landscapeSet.delete(inverse);
    }
  }
  landscape.push(
    ...[...landscapeSet].sort((a, b) => (parseRatioValue(a) ?? 1) - (parseRatioValue(b) ?? 1)),
  );
  return [
    { title: '主要アスペクト比', ratios: majors },
    { title: '横型', ratios: landscape },
    { title: '縦型', ratios: portrait },
  ].filter((section) => section.ratios.length > 0);
}

/**
 * アスペクト比一覧の全画面モーダル。
 * 各比率を実形状の矩形でビジュアル表示し、代表的な用途 (動画 / 縦型動画 /
 * インフィード広告 等) をキャプションとして添える。
 * 選択中モデルでネイティブ出力できない比率には「近似」バッジを付ける
 * (選択自体は可能で、生成時は最も近い対応比率に丸められる — 既存挙動と同じ)。
 */
export function AspectRatioPickerModal({
  isOpen,
  onClose,
  ratios,
  selectedRatio,
  modelCode,
  modelLabel,
  modelTooltip,
  onSelect,
}: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const sections = useMemo(() => buildSections(ratios), [ratios]);

  if (!isOpen) return null;

  const hasApprox = sections.some((section) => section.ratios.some((ratio) => !isAspectRatioSupportedBy(modelCode, ratio)));

  return createPortal(
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className={`modal-card ${styles.panel}`}
        role="dialog"
        aria-modal="true"
        aria-label="アスペクト比を選択"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`modal-header ${styles.header}`}>
          <h2 className="modal-title">アスペクト比を選択</h2>
          <span className={styles.modelArea}>
            <span className={styles.modelCaption}>生成モデル</span>
            {modelTooltip ? (
              <Tooltip content={modelTooltip} placement="bottom">
                <span className={styles.modelChip}>{modelLabel || modelCode}</span>
              </Tooltip>
            ) : (
              <span className={styles.modelChip}>{modelLabel || modelCode}</span>
            )}
          </span>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        <div className={`modal-body ${styles.body}`}>
          {sections.map((section) => (
            <div key={section.title} className={styles.section}>
              <div className={styles.sectionTitle}>
                {section.title}
                {section.title === '主要アスペクト比' && <RecommendedBadge size={13} marginRight={0} active />}
              </div>
              <div className={styles.grid}>
                {section.ratios.map((ratio) => {
                  const isSelected = selectedRatio === ratio;
                  const isNative = isAspectRatioSupportedBy(modelCode, ratio);
                  const useCase = ASPECT_RATIO_USE_CASES[ratio];
                  const resolutions = ASPECT_RATIO_EXAMPLE_RESOLUTIONS[ratio];
                  return (
                    <button
                      key={ratio}
                      type="button"
                      className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
                      aria-label={`${useCase ? `${ratio}（${useCase}）` : ratio}${VIDEO_RATIOS.has(ratio) ? '・動画向け' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => {
                        onSelect(ratio);
                        onClose();
                      }}
                    >
                      {VIDEO_RATIOS.has(ratio) && (
                        <span className={styles.videoMark} title="動画で使われるアスペクト比" aria-hidden="true">
                          <Film size={12} />
                        </span>
                      )}
                      {!isNative && <span className={styles.approxBadge}>近似</span>}
                      <span className={styles.shapeArea}>
                        <AspectRatioIcon ratio={ratio} size={40} />
                      </span>
                      {useCase && <span className={styles.useCase}>{useCase}</span>}
                      {resolutions && (
                        <span className={styles.resolutionExample}>
                          {resolutions.map(([width, height]) => (
                            <span key={`${width}x${height}`} className={styles.resolutionValue}>
                              {width}x{height}
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.footnote}>
          <span className={styles.legendItem}>
            <span className={styles.resolutionValue}>1080x1080</span>
            <span>解像度例（幅×高さ・px）</span>
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.videoMark} ${styles.legendBadge}`} aria-hidden="true">
              <Film size={12} />
            </span>
            <span>動画で使われるアスペクト比</span>
          </span>
          {hasApprox && (
            <span className={styles.legendItem}>
              <span className={`${styles.approxBadge} ${styles.legendBadge}`}>近似</span>
              <span>選択中モデルの最も近い対応比率で生成</span>
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
